class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }

  open() {
    // Setting the `open` attribute (rather than calling .click()) fires the native `toggle`
    // event the same way a real click does — everything downstream (onToggle, and for
    // HeaderMenu, the backdrop/scroll-lock/position pipeline) reacts to that event, not to how
    // the details element got opened, so no separate code path is needed for hover vs click.
    // global.js's click listener keeps aria-expanded in sync for real clicks; this path is
    // programmatic, so it's set here to match.
    this.mainDetailsToggle.setAttribute('open', '');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', true);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

const MEGA_MENU_DESKTOP_QUERY = '(min-width: 990px)';

function getMegaMenuBackdrop() {
  let backdrop = document.querySelector('.mega-menu-backdrop');
  if (!backdrop) {
    backdrop = document.createElement('div');
    backdrop.className = 'mega-menu-backdrop';
    // base.css has a generic `div:empty { display: none; }` accessibility rule (higher
    // specificity than a single class selector) that would otherwise hide this element, since
    // it's intentionally childless. Inline style beats any stylesheet rule regardless of
    // specificity, so set it directly rather than fighting that rule in CSS.
    backdrop.style.display = 'block';
    backdrop.addEventListener('click', () => {
      getOpenMegaMenus().forEach((menu) => menu.close());
    });
    document.body.appendChild(backdrop);
  }
  return backdrop;
}

function getOpenMegaMenus() {
  return Array.from(document.querySelectorAll('header-menu')).filter(
    (menu) =>
      menu.mainDetailsToggle &&
      menu.mainDetailsToggle.classList.contains('mega-menu') &&
      menu.mainDetailsToggle.hasAttribute('open')
  );
}

// global.js's own Escape handler (onKeyUpEscape) only fires for keyup events whose target is
// inside the currently open <details> — that always worked before because opening a menu (by
// clicking its summary) naturally focuses that summary too. Hover-opening a mega-menu (see
// HeaderMenu.onMegaMenuMouseEnter below) never moves focus, so that focus-scoped listener never
// sees the Escape keystroke. This one is focus-independent — it just checks whether any mega-menu
// is open — so Escape closes it regardless of how it was opened.
document.addEventListener('keydown', (event) => {
  if (event.code !== 'Escape') return;
  getOpenMegaMenus().forEach((menu) => menu.close());
});

function lockMegaMenuScroll() {
  const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
  document.body.classList.add('overflow-hidden');
  if (scrollbarWidth > 0) document.body.style.paddingRight = `${scrollbarWidth}px`;
}

function unlockMegaMenuScroll() {
  document.body.classList.remove('overflow-hidden');
  document.body.style.paddingRight = '';
}

const MEGA_MENU_HOVER_CLOSE_DELAY = 200;

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');

    // Hover-to-open, mega-menus only (plain dropdowns like "Shop by Price" keep click/tap-only,
    // matching this theme's existing scope decision to treat mega-menus as a distinct,
    // richer-interaction component). The listeners live on <header-menu> itself, not just the
    // summary — it wraps both the trigger and the panel, so moving the mouse from one into the
    // other never fires mouseleave, only leaving the whole component does.
    if (this.mainDetailsToggle.classList.contains('mega-menu')) {
      this.addEventListener('mouseenter', this.onMegaMenuMouseEnter.bind(this));
      this.addEventListener('mouseleave', this.onMegaMenuMouseLeave.bind(this));
    }
  }

  onMegaMenuMouseEnter() {
    if (!window.matchMedia(MEGA_MENU_DESKTOP_QUERY).matches) return;
    clearTimeout(this.megaMenuCloseTimeout);
    if (!this.mainDetailsToggle.hasAttribute('open')) this.open();
  }

  onMegaMenuMouseLeave() {
    if (!window.matchMedia(MEGA_MENU_DESKTOP_QUERY).matches) return;
    // Delayed, not immediate: without this, briefly crossing a gap between the trigger and the
    // panel (or just overshooting the trigger's edge) would slam the menu shut given how easy
    // that is to do with real mouse movement — the delay is cancelled by onMegaMenuMouseEnter
    // above if the mouse re-enters before it fires.
    this.megaMenuCloseTimeout = setTimeout(() => this.close(), MEGA_MENU_HOVER_CLOSE_DELAY);
  }

  onToggle() {
    super.onToggle();

    if (this.header) {
      this.header.preventHide = this.mainDetailsToggle.open;

      if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') === '') {
        document.documentElement.style.setProperty(
          '--header-bottom-position-desktop',
          `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
        );
      }
    }

    if (!this.mainDetailsToggle.classList.contains('mega-menu')) return;

    if (this.mainDetailsToggle.hasAttribute('open')) {
      // Only gate the open path on the breakpoint — the close path must always run its
      // cleanup (backdrop/scroll-lock/body classes) even if the viewport has already crossed
      // below 990px by the time this fires (e.g. resize-forced-close), otherwise that state
      // is left stuck on.
      if (window.matchMedia(MEGA_MENU_DESKTOP_QUERY).matches) this.onMegaMenuOpen();
    } else {
      this.onMegaMenuClose();
    }
  }

  onMegaMenuOpen() {
    // Only one mega menu open at a time.
    getOpenMegaMenus().forEach((menu) => {
      if (menu !== this) menu.close();
    });

    document.body.classList.add('mega-menu-is-open');
    const backdrop = getMegaMenuBackdrop();
    requestAnimationFrame(() => backdrop.classList.add('mega-menu-backdrop--visible'));
    lockMegaMenuScroll();
    this.updateMegaMenuPosition();
    this.updatePanelFit();

    if (!HeaderMenu.resizeListenerAdded) {
      // Throttled to one rAF per frame: a plain `resize` listener can fire dozens of times per
      // second during a drag-resize, and onResize() calls getBoundingClientRect() (forces a
      // synchronous layout) — without this, that's layout thrashing on every single event.
      let resizeRafPending = false;
      window.addEventListener('resize', () => {
        if (resizeRafPending) return;
        resizeRafPending = true;
        requestAnimationFrame(() => {
          resizeRafPending = false;
          HeaderMenu.onResize();
        });
      });
      HeaderMenu.resizeListenerAdded = true;
    }
  }

  onMegaMenuClose() {
    if (getOpenMegaMenus().length > 0) return;

    document.body.classList.remove('mega-menu-is-open');
    const backdrop = document.querySelector('.mega-menu-backdrop');
    if (backdrop) backdrop.classList.remove('mega-menu-backdrop--visible');
    unlockMegaMenuScroll();
  }

  updateMegaMenuPosition() {
    const summary = this.mainDetailsToggle.querySelector('summary');
    if (!summary || !this.header) return;
    const summaryRect = summary.getBoundingClientRect();
    const headerRect = this.header.getBoundingClientRect();

    // .mega-menu__content's containing block is .header-wrapper (needed so left:0/right:0 +
    // max-width can center the panel across the full header width, not just the trigger's own
    // width) — but that means its default `top: 100%` sits at the bottom of the *entire*
    // header bar, which is taller than just the nav-link row whenever the header has its own
    // padding/logo above that row. The plain dropdown (.list-menu--disclosure in
    // component-list-menu.css) doesn't have this problem — it uses position:absolute with no
    // top/left set at all, so it naturally sits at its own "static position" right below its
    // summary. This computes the equivalent explicit offset so the mega-menu starts at the
    // same height: the trigger's own bottom edge, not the header's.
    const topOffset = summaryRect.bottom - headerRect.top;
    this.mainDetailsToggle.style.setProperty('--mega-menu-top', `${topOffset}px`);
  }

  // Fixed max-height breakpoints can't reliably predict whether a panel overflows — its top
  // position varies (sticky header state, etc.) and so does its content height, so this measures
  // for real instead of guessing. [data-fit-panel] (currently just .brand-panel) opts a panel
  // into a set of progressively more compact CSS tiers (.is-compact, then .is-ultra-compact —
  // see assets/section-mega-menu-brand.css); this tries each one in turn until the panel's real
  // scrollHeight fits the real available space, leaving at least a 5px gap at the bottom of the
  // viewport.
  updatePanelFit() {
    const panel = this.content.querySelector('[data-fit-panel]');
    if (!panel) return;

    const BOTTOM_GAP = 5;
    panel.classList.remove('is-compact', 'is-ultra-compact');
    // Reset before measuring, not just before applying: a stale max-height/scroll position left
    // over from a previous (shorter) open would otherwise throw off both the top offset below
    // and which tier looks necessary.
    this.content.style.maxHeight = '';
    this.content.style.overflowY = '';
    this.content.scrollTop = 0;

    const available = window.innerHeight - panel.getBoundingClientRect().top - BOTTOM_GAP;

    if (panel.scrollHeight > available) panel.classList.add('is-compact');
    if (panel.scrollHeight > available) panel.classList.add('is-ultra-compact');

    // Whatever tier was just reached, this is the hard guarantee: if the content still doesn't
    // fit even at the most compact tier (e.g. an extremely short window), scroll internally
    // rather than let the panel run off the bottom of the screen with no way to reach the rest
    // of it — opening a mega menu locks body scroll, so there'd otherwise be no way to get there.
    if (panel.scrollHeight > available) {
      this.content.style.maxHeight = `${available}px`;
      this.content.style.overflowY = 'auto';
    }
  }

  static onResize() {
    if (!window.matchMedia(MEGA_MENU_DESKTOP_QUERY).matches) {
      getOpenMegaMenus().forEach((menu) => menu.close());
      return;
    }
    getOpenMegaMenus().forEach((menu) => {
      menu.updateMegaMenuPosition();
      menu.updatePanelFit();
    });
  }
}

customElements.define('header-menu', HeaderMenu);
